'use client';

import Image from '@tiptap/extension-image';
import { ReactNodeViewRenderer } from '@tiptap/react';

import { ImageNodeView } from '../_components/ImageNodeView';

export interface CustomImageOptions {
  isBorderRadius: boolean;
  maxWidth: number | null;
  align: 'left' | 'center' | 'right';
}

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    customImage: {
      setCustomImage: (options: CustomImageOptions) => ReturnType;
    };
  }
}

export const CustomImage = Image.extend({
  addCommands() {
    return {
      ...this.parent?.(),
      setCustomImage:
        ({ isBorderRadius, maxWidth, align }) =>
        ({ chain }) => {
          const classNames: string[] = [];
          const styles: string[] = [];

          if (isBorderRadius) {
            classNames.push('rounded-medium');
          }

          if (typeof maxWidth === 'number') {
            styles.push(`max-width: ${maxWidth}px;`);
          }

          if (align === 'center') {
            classNames.push('mx-auto');
          }

          if (align === 'right') {
            classNames.push('ml-auto');
          }

          return chain()
            .updateAttributes('image', {
              class: classNames.length === 0 ? undefined : classNames.join(' '),
              style: styles.length === 0 ? undefined : styles.join(' '),
            })
            .run();
        },
    };
  },
  addAttributes() {
    return {
      ...this.parent?.(),
      src: {
        default: null,
      },
      href: {
        default: null,
      },
      target: {
        default: null,
      },
      rel: {
        default: null,
      },
      class: {
        default: null,
      },
      style: {
        default: null,
      },
    };
  },
  parseHTML() {
    return [
      {
        tag: 'img[src]',
        getAttrs: (node) => {
          if (typeof node === 'string') return {};

          const element = node as HTMLElement;
          const linkElement = element.closest('a');

          return {
            src: element.getAttribute('src'),
            href: linkElement?.getAttribute('href') || null,
            target: linkElement?.getAttribute('target') || null,
            rel: linkElement?.getAttribute('rel') || null,
          };
        },
      },
    ];
  },
  renderHTML({ HTMLAttributes }) {
    const { href, target, rel, ...rest } = HTMLAttributes;

    if (href) {
      return ['a', { href, target, rel }, ['img', rest]];
    }

    return ['img', rest];
  },
  addNodeView() {
    return ReactNodeViewRenderer(ImageNodeView);
  },
}).configure({
  HTMLAttributes: {
    class: 'w-full',
  },
});
